import { gsap } from 'gsap'
import type { Point } from '@/types'

const SVG_NS = 'http://www.w3.org/2000/svg'

export class AnimationService {
  private cookieSVG: SVGSVGElement
  private fxLayer: SVGGElement

  constructor(cookieSVG: SVGSVGElement) {
    this.cookieSVG = cookieSVG
    this.fxLayer = this.initializeFxLayer()
  }

  private initializeFxLayer(): SVGGElement {
    let fxLayer = this.cookieSVG.querySelector('#fxLayer') as SVGGElement
    if (!fxLayer) {
      fxLayer = document.createElementNS(SVG_NS, 'g')
      fxLayer.setAttribute('id', 'fxLayer')
      fxLayer.setAttribute('pointer-events', 'none')
      this.cookieSVG.appendChild(fxLayer)
    }
    return fxLayer
  }

  ripple(point: Point): void {
    const circle = document.createElementNS(SVG_NS, 'circle')
    circle.setAttribute('cx', point.x.toString())
    circle.setAttribute('cy', point.y.toString())
    circle.setAttribute('r', '0')
    circle.setAttribute('fill', 'none')
    circle.setAttribute('stroke', '#00000022')
    circle.setAttribute('stroke-width', '2')

    this.cookieSVG.appendChild(circle)

    gsap.to(circle, {
      attr: { r: 28 },
      opacity: 0,
      duration: 0.4,
      ease: 'power1.out',
      onComplete: () => circle.remove(),
    })
  }

  tapBurst(point: Point, intensity = 1): void {
    const colors = ['#E7A867', '#C8894A', '#A96E34']
    const count = 2 + Math.min(intensity, 2) // Limit particle count
    const { x: cx, y: cy } = point

    const baseAngle = Math.atan2(cy - 110, cx - 100)

    for (let i = 0; i < count; i++) {
      const angle = baseAngle + gsap.utils.random(-0.4, 0.4)
      const distance = gsap.utils.random(8, 16 + intensity * 3) // Much smaller spread
      const targetX = cx + Math.cos(angle) * distance
      const targetY = cy + Math.sin(angle) * distance + gsap.utils.random(2, 8)

      const particle = document.createElementNS(SVG_NS, 'circle')
      particle.setAttribute('cx', cx.toString())
      particle.setAttribute('cy', cy.toString())
      particle.setAttribute('r', gsap.utils.random(1.2, 2.0).toString()) // Smaller particles
      particle.setAttribute('fill', colors[i % colors.length])
      particle.setAttribute('filter', 'url(#crumbShadow)')

      this.fxLayer.appendChild(particle)

      gsap.to(particle, {
        duration: 0.18 + Math.random() * 0.1,
        attr: { cx: targetX, cy: targetY },
        ease: 'power2.out',
        onComplete: () => {
          gsap.to(particle, {
            duration: 0.2,
            attr: { cy: targetY + gsap.utils.random(6, 15) },
            opacity: 0,
            ease: 'power2.in',
            onComplete: () => particle.remove(),
          })
        },
      })
    }
  }

  crackBurst(cx = 100, cy = 110): void {
    const colors = ['#F4C792', '#E7A867', '#C8894A', '#D4B373', '#F2D49B']
    const count = 16 // Reduced particle count for better performance

    for (let i = 0; i < count; i++) {
      const angle = gsap.utils.random(0, Math.PI * 2)
      const distance = gsap.utils.random(18, 35) // Much smaller spread to prevent overflow
      const targetX = cx + Math.cos(angle) * distance
      const targetY = cy + Math.sin(angle) * distance + gsap.utils.random(4, 12)

      const particle = document.createElementNS(SVG_NS, 'circle')
      particle.setAttribute('cx', cx.toString())
      particle.setAttribute('cy', cy.toString())
      particle.setAttribute('r', gsap.utils.random(1.8, 3.0).toString()) // Smaller particles
      particle.setAttribute('fill', colors[i % colors.length])
      particle.setAttribute('filter', 'url(#crumbShadow)')

      this.fxLayer.appendChild(particle)

      // Two-stage animation: burst out, then fall down
      gsap.to(particle, {
        duration: gsap.utils.random(0.3, 0.5),
        attr: { cx: targetX, cy: targetY },
        ease: 'power2.out',
        onComplete: () => {
          // Second stage: fall down with gravity
          gsap.to(particle, {
            duration: gsap.utils.random(0.4, 0.7),
            attr: { cy: targetY + gsap.utils.random(20, 40) },
            opacity: 0,
            ease: 'power2.in',
            onComplete: () => particle.remove(),
          })
        },
      })
    }
  }

  wiggleCookie(intensity = 1): Promise<void> {
    // Much more subtle trembling to prevent overflow
    const scales = [1.025, 1.035, 1.045, 1.055]
    const rotations = [4, 6, 8, 10] // stronger rotation
    const durations = [0.1, 0.12, 0.14, 0.16]
    
    const scale = scales[Math.min(intensity - 1, 3)] || 1.005
    const rotation = rotations[Math.min(intensity - 1, 3)] || 1
    const duration = durations[Math.min(intensity - 1, 3)] || 0.12

    gsap.killTweensOf(this.cookieSVG)

    return new Promise((resolve) => {
      const wiggleTween = gsap.fromTo(
        this.cookieSVG,
        { scale: 1, rotation: 0 },
        {
          scale,
          rotation: gsap.utils.random(-rotation, rotation),
          duration,
          yoyo: true,
          repeat: 2,
          ease: 'power2.inOut'
        }
      )

      wiggleTween.eventCallback('onComplete', () => {
        gsap.set(this.cookieSVG, { scale: 1, rotation: 0 })
        resolve()
      })

      // Add much more subtle glow effect
      this.addCookieGlow(intensity)
    })
  }

  private addCookieGlow(intensity: number): void {
    const cookieContainer = this.cookieSVG.parentElement
    if (cookieContainer) {
      // Much more subtle glow to prevent overflow
      const glowSizes = [3, 5, 8, 12] // Much smaller glow sizes
      const glowColors = [
        'rgba(255, 200, 100, 0.2)', // Very light golden
        'rgba(255, 180, 80, 0.3)',  // Light orange
        'rgba(255, 150, 60, 0.4)',  // Medium orange
        'rgba(255, 120, 40, 0.5)'   // Moderate orange-red
      ]
      
      const glowSize = glowSizes[Math.min(intensity - 1, 3)]
      const glowColor = glowColors[Math.min(intensity - 1, 3)]
      
      gsap.to(cookieContainer, {
        filter: `drop-shadow(0 0 ${glowSize}px ${glowColor})`,
        duration: 0.15,
        ease: 'power2.out',
        onComplete: () => {
          // Keep the glow for a shorter moment, then fade
          gsap.to(cookieContainer, {
            filter: 'none',
            duration: 0.5,
            ease: 'power2.out',
            delay: 0.2
          })
        }
      })
    }
  }



  screenShake(): void {
  const target = (document.getElementById('app') ?? document.body) as HTMLElement

  gsap.killTweensOf(target)

  gsap.fromTo(
    target,
    { x: 0, y: 0 },
    {
      x: gsap.utils.random(-1, 1),
      y: gsap.utils.random(-1, 1),
      duration: 0.08,
      repeat: 2,
      yoyo: true,
      ease: 'power1.inOut',
      onComplete: () => {
        // IMPORTANT: remove the transform entirely (translate(0,0) still breaks fixed on iOS webviews)
        gsap.set(target, { clearProps: 'transform' })
        // extra safety if target is body in dev
        gsap.set(document.body, { clearProps: 'transform' })
      },
    }
  )
}

  openCookie(): void {
    gsap.to('#leftGroup', {
      rotation: -38,
      transformOrigin: '100px 110px',
      duration: 0.6,
      ease: 'back.in',
    })

    gsap.to('#rightGroup', {
      rotation: 38,
      transformOrigin: '100px 110px',
      duration: 0.6,
      ease: 'back.in',
    })

    gsap.to('#leftWrap', { x: -36, duration: 0.6, ease: 'back.in' })
    gsap.to('#rightWrap', { x: 36, duration: 0.6, ease: 'back.in' })
  }

  resetCookie(): Promise<void> {
    return new Promise((resolve) => {
      gsap.to('#leftGroup', {
        rotation: 0,
        duration: 0.5,
        ease: 'back.out',
      })

      gsap.to('#rightGroup', {
        rotation: 0,
        duration: 0.5,
        ease: 'back.out',
      })

      gsap.to('#leftWrap', { x: 0, duration: 0.5, ease: 'back.out' })

      gsap.to('#rightWrap', {
        x: 0,
        duration: 0.5,
        ease: 'back.out',
        onComplete: resolve,
      })
    })
  }

  showFortune(element: HTMLElement): void {
    element.style.display = 'block'
    
    // More dramatic fortune reveal animation
    gsap.fromTo(element, 
      { 
        opacity: 0, 
        y: 20, 
        scale: 0.9,
        rotationX: -15
      }, 
      { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        rotationX: 0,
        duration: 0.6, 
        ease: 'back.out(1.2)', 
        delay: 0.3 
      }
    )
    
    // Add subtle glow effect to fortune text
    gsap.fromTo(element,
      { textShadow: '0 0 0px rgba(255, 215, 0, 0)' },
      { 
        textShadow: '0 0 8px rgba(255, 215, 0, 0.3)',
        duration: 0.8,
        delay: 0.5,
        yoyo: true,
        repeat: 1
      }
    )
  }

  hideFortune(element: HTMLElement): void {
    gsap.to(element, { 
      opacity: 0, 
      duration: 0.25,
      ease: 'power2.out',
      onComplete: () => {
        element.style.display = 'none'
      }
    })
  }

  showFortuneActions(element: HTMLElement): void {
    element.style.display = 'block'
    gsap.fromTo(element, { opacity: 0, scale: 0.95, y: 4 }, { opacity: 1, scale: 1, y: 0, delay: 0.4, duration: 0.3, ease: 'power2.out' })
  }

  hideFortuneActions(element: HTMLElement): void {
    gsap.to(element, { 
      opacity: 0, 
      scale: 0.8, 
      duration: 0.25,
      onComplete: () => {
        element.style.display = 'none'
      }
    })
  }

  // New rhythm system animations
  pulseRhythmIndicator(indicator: SVGElement, cycleDuration: number): void {
    gsap.set(indicator, { opacity: 1 })
    
    gsap.to(indicator, {
      attr: { r: 105 },
      opacity: 0.8,
      duration: cycleDuration / 2000, // Half cycle to peak
      ease: 'power2.inOut',
      yoyo: true,
      repeat: -1
    })
  }

  showFeedbackText(element: HTMLElement, feedback: string): void {
    const scales = {
      perfect: 1.5,
      good: 1.3,
      ok: 1.1,
      miss: 1.0
    }

    const scale = scales[feedback as keyof typeof scales] || 1.0

    gsap.killTweensOf(element)
    gsap.set(element, { opacity: 1, scale: 0.5, y: 0 })
    
    gsap.to(element, {
      scale,
      duration: 0.2,
      ease: 'back.out(1.7)'
    })

    gsap.to(element, {
      y: -20,
      opacity: 0,
      duration: 0.8,
      delay: 0.3,
      ease: 'power2.out'
    })
  }

  missEffect(point: Point): void {
    // Create a red X effect for misses
    const x1 = document.createElementNS('http://www.w3.org/2000/svg', 'line')
    const x2 = document.createElementNS('http://www.w3.org/2000/svg', 'line')
    
    const size = 8
    x1.setAttribute('x1', (point.x - size).toString())
    x1.setAttribute('y1', (point.y - size).toString())
    x1.setAttribute('x2', (point.x + size).toString())
    x1.setAttribute('y2', (point.y + size).toString())
    x1.setAttribute('stroke', '#FF6B6B')
    x1.setAttribute('stroke-width', '3')
    x1.setAttribute('stroke-linecap', 'round')

    x2.setAttribute('x1', (point.x + size).toString())
    x2.setAttribute('y1', (point.y - size).toString())
    x2.setAttribute('x2', (point.x - size).toString())
    x2.setAttribute('y2', (point.y + size).toString())
    x2.setAttribute('stroke', '#FF6B6B')
    x2.setAttribute('stroke-width', '3')
    x2.setAttribute('stroke-linecap', 'round')

    this.fxLayer.appendChild(x1)
    this.fxLayer.appendChild(x2)

    gsap.fromTo([x1, x2], 
      { opacity: 0, scale: 0.5 },
      { 
        opacity: 1, 
        scale: 1, 
        duration: 0.2,
        ease: 'back.out(1.7)',
        onComplete: () => {
          gsap.to([x1, x2], {
            opacity: 0,
            duration: 0.3,
            delay: 0.2,
            onComplete: () => {
              x1.remove()
              x2.remove()
            }
          })
        }
      }
    )
  }

  cleanupOngoingAnimations(): void {
    // Kill all GSAP animations on the cookie SVG and related elements
    gsap.killTweensOf(this.cookieSVG)
    gsap.killTweensOf('#leftGroup')
    gsap.killTweensOf('#rightGroup')
    gsap.killTweensOf('#leftWrap')
    gsap.killTweensOf('#rightWrap')
    
    // Clean up rhythm indicator
    const rhythmIndicator = this.cookieSVG.querySelector('#rhythmIndicator')
    if (rhythmIndicator) {
      gsap.killTweensOf(rhythmIndicator)
    }
    
    // Clean up any remaining particles in the fx layer
    if (this.fxLayer) {
      const particles = this.fxLayer.querySelectorAll('circle, line')
      particles.forEach(particle => {
        gsap.killTweensOf(particle)
        particle.remove()
      })
    }
    
    // Remove any glow effects from cookie container
    const cookieContainer = this.cookieSVG.parentElement
    if (cookieContainer) {
      gsap.killTweensOf(cookieContainer)
      cookieContainer.style.filter = 'none'
    }
    
    // Clean up any ripple effects that might still be on the SVG
    const ripples = this.cookieSVG.querySelectorAll('circle:not(#fxLayer circle):not(#rhythmIndicator)')
    ripples.forEach(ripple => {
      gsap.killTweensOf(ripple)
      ripple.remove()
    })
    
    // Reset cookie transform properties to prevent any lingering effects
    gsap.set(this.cookieSVG, { 
      scale: 1, 
      rotation: 0, 
      x: 0, 
      y: 0,
      clearProps: 'transform'
    })
    
    // Kill any body animations (screen shake)
    gsap.killTweensOf(document.body)
    gsap.set(document.body, { x: 0, y: 0, clearProps: 'transform' })
  }
}
